import { handleActions } from 'redux-actions';
import uuid from 'uuid/v4';
import { removeFromObject } from '../utilities/common';
import { getInitialFromPackage, changeUserData, updateCalculationJourney } from '../utilities/userdata/userDataUtils';
import * as userDataTypes from '../constants/UserData';
import * as apiTypes from '../constants/Api';
import * as personalTypes from '../constants/personal-api';
import * as types from '../constants/App';
import { apiHandler } from './Api';

const initialState = {
  loaderProgress: 0,
  packageId: null,
  isCalculatingPrice: false,
  isPurchase: false,
  gTransactionId: uuid(),
  calculationJourneyStarted: false,
};

const userDataHandler = (state, action) => {
  switch (action.type) {
    case (types.APP_ERROR): {
      const {
        payload: {
          currentPageId,
          currentSectionId,
          currentStepId,
          response: {
            data: {
              errorDetails: {
                errorCode,
                errorText,
              },
            },
          },
        },
      } = action;
      return {
        ...state,
        errorCode,
        errorText,
        currentPageId,
        currentSectionId,
        currentStepId,
      };
    }
    case (types.APP_INITIAL): {
      const { payload } = action;
      return {
        ...state,
        ...payload.userData,
      };
    }
    case (userDataTypes.USERDATA_FORM_SUBMIT): {
      const { payload } = action;
      const ignoreValues = ['loaderProgress', 'quotes'];
      const newPayload = removeFromObject(payload, ignoreValues);

      return {
        ...state,
        ...newPayload,
      };
    }
    case (userDataTypes.USERDATA_UPDATE_LOADER): {
      const { payload } = action;
      let { loaderProgress } = state;

      // Add up the payload to the current progress
      loaderProgress += payload;
      // If loaderProgress is higher than 0.97, then set to 1
      loaderProgress = loaderProgress >= 0.97 ? 1 : loaderProgress;

      return {
        ...state,
        loaderProgress,
      };
    }
    case (userDataTypes.USERDATA_RESET_LOADER): {
      return {
        ...state,
        loaderProgress: 0,
      };
    }
    case (userDataTypes.USERDATA_INITIAL): {
      const { payload } = action;
      return {
        ...getInitialFromPackage(payload),
        ...state,
      };
    }
    case (userDataTypes.USERDATA_CHANGE): {
      const { payload } = action;
      return changeUserData(payload, state);
    }
    case (userDataTypes.CALCULATION_JOURNEY_STARTED): {
      const newPayload = updateCalculationJourney(state);
      return {
        ...state,
        ...newPayload,
      };
    }
    case (userDataTypes.UPDATE_COVERAGE_LIST):
    case (userDataTypes.UPDATE_INSURANCE_LIST):
    case (userDataTypes.UPDATE_ANCHOR_TEXT):
    case (userDataTypes.UPDATE_ANCHOR_CHECKBOX):
    case (userDataTypes.UPDATE_TRANSLATION_CONFIRMATION):
    case (userDataTypes.USERDATA_TOKEN_UPDATE): {
      const { payload } = action;
      return {
        ...state,
        ...payload,
      };
    }
    default:
      return {};
  }
};

const UserData = handleActions({
  [types.APP_ERROR]: (state, action) => ({
    ...state,
    ...userDataHandler(state, action),
  }),
  [types.APP_INITIAL]: (state, action) => ({
    ...state,
    ...userDataHandler(state, action),
  }),
  [userDataTypes.USERDATA_INITIAL]: (state, action) => ({
    ...state,
    ...userDataHandler(state, action),
  }),
  [userDataTypes.USERDATA_FORM_SUBMIT]: (state, action) => ({
    ...state,
    ...userDataHandler(state, action),
  }),
  [userDataTypes.USERDATA_UPDATE_LOADER]: (state, action) => ({
    ...state,
    ...userDataHandler(state, action),
  }),
  [userDataTypes.USERDATA_RESET_LOADER]: (state, action) => ({
    ...state,
    ...userDataHandler(state, action),
  }),
  [userDataTypes.USERDATA_CHANGE]: (state, action) => ({
    ...userDataHandler(state, action),
  }),
  [userDataTypes.USERDATA_TOKEN_UPDATE]: (state, action) => ({
    ...userDataHandler(state, action),
  }),
  [userDataTypes.CALCULATION_JOURNEY_STARTED]: (state, action) => ({
    ...state,
    ...userDataHandler(state, action),
  }),
  [userDataTypes.UPDATE_COVERAGE_LIST]: (state, action) => ({
    ...state,
    ...userDataHandler(state, action),
  }),
  [userDataTypes.UPDATE_INSURANCE_LIST]: (state, action) => ({
    ...state,
    ...userDataHandler(state, action),
  }),
  [apiTypes.OAUTH_REQUEST]: (state, action) => ({
    ...state,
    ...apiHandler(state, action),
  }),
  [apiTypes.OAUTH_RECEIVE]: (state, action) => ({
    ...state,
    ...apiHandler(state, action),
  }),
  [apiTypes.SERVICES_LOADING]: (state, action) => ({
    ...state,
    ...apiHandler(state, action),
  }),
  [apiTypes.SERVICES_LOADED]: (state, action) => ({
    ...state,
    ...apiHandler(state, action),
  }),
  [apiTypes.CUSTOMER_CREATE]: (state, action) => ({
    ...state,
    ...apiHandler(state, action),
  }),
  [apiTypes.CUSTOMER_CREATED]: (state, action) => ({
    ...state,
    ...apiHandler(state, action),
  }),
  [apiTypes.CUSTOMER_UPDATE]: (state, action) => ({
    ...state,
    ...apiHandler(state, action),
  }),
  [apiTypes.CUSTOMER_UPDATED]: (state, action) => ({
    ...state,
    ...apiHandler(state, action),
  }),
  [apiTypes.CUSTOMER_GET_ACTIVATION_DATE]: (state, action) => ({
    ...state,
    ...apiHandler(state, action),
  }),
  [apiTypes.CUSTOMER_RECEIVED_ACTIVATION_DATE]: (state, action) => ({
    ...state,
    ...apiHandler(state, action),
  }),
  [apiTypes.CUSTOMER_RECEIVED_ACTIVATION_DATES]: (state, action) => ({
    ...state,
    ...apiHandler(state, action),
  }),
  [apiTypes.CAR_UPDATE_MILEAGE]: (state, action) => ({
    ...state,
    ...apiHandler(state, action),
  }),
  [apiTypes.CUSTOMER_QUOTE]: (state, action) => ({
    ...state,
    ...apiHandler(state, action),
  }),
  [apiTypes.CUSTOMER_QUOTED]: (state, action) => ({
    ...state,
    ...apiHandler(state, action),
  }),
  [apiTypes.UPDATE_QUOTE]: (state, action) => ({
    ...state,
    ...apiHandler(state, action),
  }),
  [apiTypes.UPDATED_QUOTE]: (state, action) => ({
    ...state,
    ...apiHandler(state, action),
  }),
  [apiTypes.CAR_REGISTRATION_NUMBER_VALID]: (state, action) => ({
    ...state,
    ...apiHandler(state, action),
  }),
  [apiTypes.CAR_REGISTRATION_NUMBER_VALIDATED]: (state, action) => ({
    ...state,
    ...apiHandler(state, action),
  }),
  [apiTypes.CAR_CREATE]: (state, action) => ({
    ...state,
    ...apiHandler(state, action),
  }),
  [apiTypes.CAR_CREATED]: (state, action) => ({
    ...state,
    ...apiHandler(state, action),
  }),
  [apiTypes.CAR_UPDATE]: (state, action) => ({
    ...state,
    ...apiHandler(state, action),
  }),
  [apiTypes.CAR_UPDATED]: (state, action) => ({
    ...state,
    ...apiHandler(state, action),
  }),
  [apiTypes.CAR_PREPARE]: (state, action) => ({
    ...state,
    ...apiHandler(state, action),
  }),
  [apiTypes.CAR_PREPARED]: (state, action) => ({
    ...state,
    ...apiHandler(state, action),
  }),
  [apiTypes.PAYMENT_CREATED]: (state, action) => ({
    ...state,
    ...apiHandler(state, action),
  }),
  [apiTypes.TRAVEL_AUX_GET_PRICE_REQUEST]: (state, action) => ({
    ...state,
    ...apiHandler(state, action),
  }),
  [apiTypes.TRAVEL_AUX_GET_PRICE_REQUESTED]: (state, action) => ({
    ...state,
    ...apiHandler(state, action),
  }),
  [apiTypes.TRAVEL_UPDATE]: (state, action) => ({
    ...state,
    ...apiHandler(state, action),
  }),
  [apiTypes.TRAVEL_CREATED]: (state, action) => ({
    ...state,
    ...apiHandler(state, action),
  }),
  [apiTypes.TRAVEL_UPDATE]: (state, action) => ({
    ...state,
    ...apiHandler(state, action),
  }),
  [apiTypes.TRAVEL_UPDATED]: (state, action) => ({
    ...state,
    ...apiHandler(state, action),
  }),
  [apiTypes.TRAVEL_PREPARE]: (state, action) => ({
    ...state,
    ...apiHandler(state, action),
  }),
  [apiTypes.TRAVEL_PREPARED]: (state, action) => ({
    ...state,
    ...apiHandler(state, action),
  }),
  [apiTypes.PURCHASE]: (state, action) => ({
    ...state,
    ...apiHandler(state, action),
  }),
  [apiTypes.PURCHASED]: (state, action) => ({
    ...state,
    ...apiHandler(state, action),
  }),
  [apiTypes.HOUSEHOLD_UPDATE]: (state, action) => ({
    ...state,
    ...apiHandler(state, action),
  }),
  [apiTypes.HOUSEHOLD_CREATED]: (state, action) => ({
    ...state,
    ...apiHandler(state, action),
  }),
  [apiTypes.HOUSEHOLD_UPDATE]: (state, action) => ({
    ...state,
    ...apiHandler(state, action),
  }),
  [apiTypes.HOUSEHOLD_UPDATED]: (state, action) => ({
    ...state,
    ...apiHandler(state, action),
  }),
  [apiTypes.HOUSEHOLD_ADDRESS_RECEIVE_ERROR]: (state, action) => ({
    ...state,
    ...apiHandler(state, action),
  }),
  [apiTypes.HOUSEHOLD_ADDRESS_RECEIVED_ERROR]: (state, action) => ({
    ...state,
    ...apiHandler(state, action),
  }),
  [apiTypes.HOUSEHOLD_ADDRESS_RECEIVE]: (state, action) => ({
    ...state,
    ...apiHandler(state, action),
  }),
  [apiTypes.HOUSEHOLD_ADDRESS_RECEIVED]: (state, action) => ({
    ...state,
    ...apiHandler(state, action),
  }),
  [apiTypes.HOUSEHOLD_PREPARE]: (state, action) => ({
    ...state,
    ...apiHandler(state, action),
  }),
  [apiTypes.HOUSEHOLD_PREPARED]: (state, action) => ({
    ...state,
    ...apiHandler(state, action),
  }),
  [apiTypes.HOUSEHOLD_COV_STATE_UPDATED]: (state, action) => ({
    ...state,
    ...apiHandler(state, action),
  }),
  [apiTypes.HOUSEHOLD_AUX_UPDATED]: (state, action) => ({
    ...state,
    ...apiHandler(state, action),
  }),
  [apiTypes.DEPOSIT_COMPANY_INFO_VALID]: (state, action) => ({
    ...state,
    ...apiHandler(state, action),
  }),
  [apiTypes.DEPOSIT_COMPANY_INFO_VALIDATED]: (state, action) => ({
    ...state,
    ...apiHandler(state, action),
  }),
  [apiTypes.DEPOSIT_CREATE]: (state, action) => ({
    ...state,
    ...apiHandler(state, action),
  }),
  [apiTypes.DEPOSIT_CREATED]: (state, action) => ({
    ...state,
    ...apiHandler(state, action),
  }),
  [apiTypes.DEPOSIT_UPDATE]: (state, action) => ({
    ...state,
    ...apiHandler(state, action),
  }),
  [apiTypes.DEPOSIT_UPDATE]: (state, action) => ({
    ...state,
    ...apiHandler(state, action),
  }),
  [apiTypes.DEPOSIT_PREPARE]: (state, action) => ({
    ...state,
    ...apiHandler(state, action),
  }),
  [apiTypes.DEPOSIT_PREPARED]: (state, action) => ({
    ...state,
    ...apiHandler(state, action),
  }),
  [apiTypes.PREGNANCY_CREATE]: (state, action) => ({
    ...state,
    ...apiHandler(state, action),
  }),
  [apiTypes.PREGNANCY_CREATED]: (state, action) => ({
    ...state,
    ...apiHandler(state, action),
  }),
  [apiTypes.PREGNANCY_UPDATE]: (state, action) => ({
    ...state,
    ...apiHandler(state, action),
  }),
  [apiTypes.PREGNANCY_UPDATED]: (state, action) => ({
    ...state,
    ...apiHandler(state, action),
  }),
  [apiTypes.CVR_VALID]: (state, action) => ({
    ...state,
    ...apiHandler(state, action),
  }),
  [apiTypes.CVR_VALIDATED]: (state, action) => ({
    ...state,
    ...apiHandler(state, action),
  }),
  [personalTypes.PERSONAL_CREATE]: (state, action) => ({
    ...state,
    ...apiHandler(state, action),
  }),
  [personalTypes.PERSONAL_CREATED]: (state, action) => ({
    ...state,
    ...apiHandler(state, action),
  }),
  [apiTypes.BANK_ACCOUNT_VALID]: (state, action) => ({
    ...state,
    ...apiHandler(state, action),
  }),
  [userDataTypes.UPDATE_ANCHOR_TEXT]: (state, action) => ({
    ...state,
    ...userDataHandler(state, action),
  }),
  [userDataTypes.UPDATE_ANCHOR_CHECKBOX]: (state, action) => ({
    ...state,
    ...userDataHandler(state, action),
  }),
  [userDataTypes.UPDATE_TRANSLATION_CONFIRMATION]: (state, action) => ({
    ...state,
    ...userDataHandler(state, action),
  })
}, initialState);

export default UserData;